import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Filter, Lock, RotateCcw, Search } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { MultiSelect } from '../../../components/ui/multi-select';
import type { AdminIssuesFilters, StatusCategory } from '../../../api/adminIssuesApi';
import { STATUS_CATEGORY_KEYS } from '../../../api/adminIssuesApi';
import { useAdminIssuesFilterOptions } from '../../../hooks/useAdminIssuesFilterOptions';
import { useDebouncedSearch } from '../../../hooks/useDebouncedSearch';
import { useParentKeyInput } from '../../../hooks/useParentKeyInput';
import { UserSearchSelect } from './UserSearchSelect';


// Collapse an empty multi-select to undefined so the filter clears entirely.
const orUndefined = <T,>(values: T[]): T[] | undefined => (values.length ? values : undefined);

const ADVANCED_ID = 'issues-advanced-filters';

/**
 * What the collapsed panel hides — and only that. The search box, the status and the desk stay
 * on screen, so badging them would count what the user is already looking at. `requestType` is
 * added separately: locked by the tab, it sits in the compact bar and is not the user's choice.
 */
const ADVANCED_KEYS: (keyof AdminIssuesFilters)[] = [
  'statusCategory',
  'sector',
  'store',
  'createdFrom',
  'createdTo',
  'updatedFrom',
  'updatedTo',
  'reporterAccountId',
  'assigneeAccountId',
  'parentKey',
];

const PARENT_HINT_ID = 'issues-parent-key-hint';

const isSet = (value: unknown): boolean => (Array.isArray(value) ? value.length > 0 : !!value);

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col gap-[7px]">
    <label className="text-[12.5px] font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

/** Compact bar: the reason is written next to the lock, not only in a `title` nobody can hover. */
const LockedCompact = ({
  locked,
  hint,
  children,
}: {
  locked: boolean;
  hint: string;
  children: ReactNode;
}) =>
  locked ? (
    <span className="inline-flex items-center gap-1.5" title={hint}>
      <Lock className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
      {children}
      <span className="text-[11px] font-normal text-muted-foreground/80">{hint}</span>
    </span>
  ) : (
    <>{children}</>
  );

interface IssuesFiltersBarProps {
  filters: AdminIssuesFilters;
  onChange: (partial: Partial<AdminIssuesFilters>) => void;
  onReset: () => void;
  /** The desk picker, built by the page, as the first control of the compact row. */
  deskSelect?: ReactNode;
  /**
   * The filters the « À qualifier » tab imposes — its own definition, not the user's choice
   * (D13/D18) — or `null` off that tab. Carrying the values, not just a flag: they are shown, and
   * non-removable because dropping one would list tickets the tab is not about. Adding more is
   * allowed, so the operator can narrow the tab further.
   */
  lockedByTab?: Partial<AdminIssuesFilters> | null;
  showParentFilter?: boolean;
}

export const IssuesFiltersBar = ({
  filters,
  onChange,
  onReset,
  deskSelect,
  lockedByTab = null,
  showParentFilter = false,
}: IssuesFiltersBarProps) => {
  const { t } = useTranslation();
  // Derived, never a second prop: the tab locks exactly when it hands its filters over.
  const locked = !!lockedByTab;
  // Whether the advanced grid is unfolded is this bar's own business, not the page's.
  const [open, setOpen] = useState(false);
  // Options built from literal translation keys — the i18n static guard rejects variables.
  const statusCategoryOptions: { value: StatusCategory; label: string }[] = [
    { value: 'New', label: t('To do') },
    { value: 'Indeterminate', label: t('In progress') },
    { value: 'Done', label: t('Done') },
  ];
  const [searchInput, setSearchInput] = useDebouncedSearch(filters.search, (search) =>
    onChange({ search })
  );
  // Synced from `filters.parentKey`, so a key set by clicking a parent in the table shows here too.
  const [parentInput, setParentInput, parentMalformed] = useParentKeyInput(
    filters.parentKey,
    filters.serviceDeskId,
    (parentKey) => onChange({ parentKey })
  );
  const { data: options, isLoading: isLoadingOptions } = useAdminIssuesFilterOptions(
    filters.serviceDeskId
  );

  // Statuses live inside a category, so picking a category narrows the status list to the
  // matching ones — and drops any already-selected status now out of scope, in one onChange.
  const statusesInCategories = (categories: StatusCategory[] | undefined) =>
    (options?.statuses ?? []).filter(
      (status) => !categories?.length || categories.some((c) => STATUS_CATEGORY_KEYS[c] === status.category)
    );

  const handleCategoriesChange = (values: string[]) => {
    const categories = orUndefined(values as StatusCategory[]);
    const visible = new Set(statusesInCategories(categories).map((s) => s.id));
    const keptStatuses = (filters.status ?? []).filter((id) => visible.has(id));
    onChange({ statusCategory: categories, status: orUndefined(keptStatuses) });
  };

  const statusOptions = statusesInCategories(filters.statusCategory);

  // Selecting a sector both applies it and drops any selected store that falls
  // outside the new perimeter, in a single onChange call.
  const handleSectorsChange = (values: string[]) => {
    const sectors = orUndefined(values);
    const visible = new Set(
      (options?.stores ?? [])
        .filter((s) => !sectors || (s.sector !== null && sectors.includes(s.sector)))
        .map((s) => s.label)
    );
    const keptStores = (filters.store ?? []).filter((label) => visible.has(label));
    onChange({ sector: sectors, store: orUndefined(keptStores) });
  };

  const storeOptions = (options?.stores ?? []).filter(
    (s) => !filters.sector?.length || (s.sector !== null && filters.sector.includes(s.sector))
  );

  // Derived, never stored: a stored count is a second source of truth for the same filters.
  const advancedCount =
    ADVANCED_KEYS.filter((key) => isSet(filters[key])).length +
    (!locked && isSet(filters.requestType) ? 1 : 0);

  // On the locked tab the status is the tab's own definition, so there is nothing of the
  // user's to reset unless they set something else.
  const showReset =
    advancedCount > 0 ||
    !!searchInput.trim() ||
    !!parentInput.trim() ||
    (!locked && !!filters.status?.length);

  return (
    <div className="rounded-md bg-gray-50 p-4 dark:bg-zinc-900">
      <div className="flex flex-wrap items-center gap-[10px]">
        {deskSelect}

        <div className="relative min-w-[200px] flex-1">
          <Search
            className="pointer-events-none absolute left-[11px] top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            className="h-[38px] rounded-[9px] pl-[35px]"
            // The compact bar has no room for a visible label, so the name is carried here:
            // a placeholder alone is not an accessible name once it is typed into.
            aria-label={t('Key or summary')}
            placeholder={t('Key or summary')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        {/* Status never folds away: locked it is the tab's definition, unlocked it is the filter
            the ops team changes all day. Both are answers to "what am I looking at?". */}
        <LockedCompact locked={locked} hint={t('Set by the tab, additions allowed')}>
          <MultiSelect
            summary
            label={t('Status')}
            aria-label={
              locked ? `${t('Status')} — ${t('Set by the tab, additions allowed')}` : t('Status')
            }
            options={statusOptions.map((status) => ({ value: status.id, label: status.name }))}
            selected={filters.status ?? []}
            onChange={(values: string[]) => onChange({ status: orUndefined(values) })}
            placeholder={isLoadingOptions ? t('Loading…') : t('Any status')}
            // Locked, not disabled: a disabled trigger never opens, so the operator could not even
            // read the options — the recette's own complaint.
            lockedValues={lockedByTab?.status ?? []}
            disabled={isLoadingOptions || !statusOptions.length}
            searchable
          />
        </LockedCompact>

        {locked && (
          <LockedCompact locked hint={t('Set by the tab, additions allowed')}>
            <MultiSelect
              summary
              label={t('Request type')}
              aria-label={`${t('Request type')} — ${t('Set by the tab, additions allowed')}`}
              options={(options?.requestTypes ?? []).map((rt) => ({ value: rt.name, label: rt.name }))}
              selected={filters.requestType ?? []}
              onChange={(values: string[]) => onChange({ requestType: orUndefined(values) })}
              placeholder={isLoadingOptions ? t('Loading…') : t('Any request type')}
              lockedValues={lockedByTab?.requestType ?? []}
              disabled={isLoadingOptions || !options?.requestTypes.length}
            />
          </LockedCompact>
        )}

        <button
          type="button"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          aria-expanded={open}
          // Never point an ARIA relationship at an id behind a conditional render (R-027).
          aria-controls={open ? ADVANCED_ID : undefined}
          title={advancedCount > 0 ? t('{{count}} active filters', { count: advancedCount }) : undefined}
          className={`inline-flex h-[38px] items-center gap-[7px] rounded-[9px] border bg-background px-[13px] text-[13px] font-medium ${
            open ? 'border-foreground/20' : 'border-input'
          }`}
        >
          <Filter className="h-[15px] w-[15px]" aria-hidden="true" />
          {t('Filters')}
          {/* Plain text inside the button, never aria-hidden: the count is part of its name. */}
          {advancedCount > 0 && (
            <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[6px] bg-primary px-[5px] text-[11px] font-semibold text-primary-foreground">
              {advancedCount}
            </span>
          )}
          <ChevronDown
            className={`h-[15px] w-[15px] text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>

        {/* Nothing set, nothing to reset: an always-on button offers to undo an empty state. */}
        {showReset && (
          <button
            type="button"
            onClick={() => {
              setParentInput('');
              onReset();
            }}
            className="inline-flex h-[38px] items-center gap-[7px] rounded-[9px] border-none bg-transparent px-[13px] text-[13px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <RotateCcw className="h-[15px] w-[15px]" aria-hidden="true" />
            {t('Reset')}
          </button>
        )}
      </div>

      {open && (
        <div
          id={ADVANCED_ID}
          // A named group, so the disclosure's contents announce themselves as one set.
          role="group"
          aria-label={t('Advanced filters')}
          className="mt-[14px] grid grid-cols-1 gap-x-4 gap-y-[14px] border-t pt-4 md:grid-cols-2 lg:grid-cols-4"
        >
          <Field label={t('Category')}>
            <MultiSelect
              options={statusCategoryOptions}
              selected={filters.statusCategory ?? []}
              onChange={handleCategoriesChange}
              placeholder={t('Any category')}
            />
          </Field>

          {!locked && (
            <Field label={t('Request type')}>
              <MultiSelect
                aria-label={t('Request type')}
                options={(options?.requestTypes ?? []).map((rt) => ({ value: rt.name, label: rt.name }))}
                selected={filters.requestType ?? []}
                onChange={(values: string[]) => onChange({ requestType: orUndefined(values) })}
                placeholder={isLoadingOptions ? t('Loading…') : t('Any request type')}
                disabled={isLoadingOptions || !options?.requestTypes.length}
              />
            </Field>
          )}

          {!!options?.sectors?.length && (
            <Field label={t('Sector')}>
              <MultiSelect
                options={options.sectors.map((sector) => ({ value: sector, label: sector }))}
                selected={filters.sector ?? []}
                onChange={handleSectorsChange}
                placeholder={t('Any sector')}
                searchable
              />
            </Field>
          )}

          {!!options?.stores?.length && (
            <Field label={t('Store')}>
              <MultiSelect
                options={storeOptions.map((s) => ({ value: s.label, label: s.label }))}
                selected={filters.store ?? []}
                onChange={(values: string[]) => onChange({ store: orUndefined(values) })}
                placeholder={t('Any store')}
                searchable
              />
            </Field>
          )}

          <Field label={t('Created from')}>
            <Input
              type="date"
              value={filters.createdFrom ?? ''}
              onChange={(e) => onChange({ createdFrom: e.target.value || undefined })}
            />
          </Field>

          <Field label={t('Created to')}>
            <Input
              type="date"
              value={filters.createdTo ?? ''}
              onChange={(e) => onChange({ createdTo: e.target.value || undefined })}
            />
          </Field>

          <Field label={t('Updated from')}>
            <Input
              type="date"
              value={filters.updatedFrom ?? ''}
              onChange={(e) => onChange({ updatedFrom: e.target.value || undefined })}
            />
          </Field>

          <Field label={t('Updated to')}>
            <Input
              type="date"
              value={filters.updatedTo ?? ''}
              onChange={(e) => onChange({ updatedTo: e.target.value || undefined })}
            />
          </Field>

          {showParentFilter && (
            <Field label={t('Parent ticket')}>
              <Input
                aria-label={t('Parent ticket')}
                placeholder="ABC-123"
                value={parentInput}
                onChange={(e) => setParentInput(e.target.value)}
                aria-invalid={parentMalformed}
                aria-describedby={parentMalformed ? PARENT_HINT_ID : undefined}
              />
              {parentMalformed && (
                <p id={PARENT_HINT_ID} className="text-[12px] text-rose-600 dark:text-rose-400">
                  {t('Expected format: ABC-123')}
                </p>
              )}
            </Field>
          )}

          <Field label={t('Reporter')}>
            <UserSearchSelect
              serviceDeskId={filters.serviceDeskId}
              value={filters.reporterAccountId}
              placeholder={t('Search a reporter')}
              onChange={(accountIds) => onChange({ reporterAccountId: orUndefined(accountIds) })}
            />
          </Field>

          <Field label={t('Assignee')}>
            <UserSearchSelect
              serviceDeskId={filters.serviceDeskId}
              value={filters.assigneeAccountId}
              placeholder={t('Search an assignee')}
              onChange={(accountIds) => onChange({ assigneeAccountId: orUndefined(accountIds) })}
            />
          </Field>
        </div>
      )}
    </div>
  );
};
